import { useCallback, useEffect, useMemo } from 'react'
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil'
import {
  GET_BROADCAST_COUNT,
  IS_PREV_BROADCAST_DATA_EXIST,
  MORE_BROADCAST_LIST_INDEX,
  PREV_BROADCAST_LIST_INDEX,
  SELECTED_BROADCAST_DATE,
  currentDate,
} from 'core/store/broadcastTable'
import { formatYYYYMMDD } from 'core/util/date'

interface BroadcastWeekDays {
  /**
   * 요일
   */
  day: string
  /**
   * 일자의 Date 객체
   */
  date: Date
  /**
   * 일자 선택 여부
   */
  isSelected: boolean
  /**
   * 해당 일자에 방송이 존재하는지 여부
   */
  isEmpty: boolean
}

/**
 * 라이브 편성표에서 10개 일자를 계산하는 hook
 */
export default function useBroadcastWeekDays() {
  const resetMoreListIndex = useResetRecoilState(MORE_BROADCAST_LIST_INDEX)
  const resetPrevListIndex = useResetRecoilState(PREV_BROADCAST_LIST_INDEX)
  const resetIsPrevDataExist = useResetRecoilState(IS_PREV_BROADCAST_DATA_EXIST)

  const broadcastCount = useRecoilValue(GET_BROADCAST_COUNT)
  const [selectedDate, setSelectedDate] = useRecoilState(SELECTED_BROADCAST_DATE)
  const isToday = selectedDate.getDate() === currentDate.getDate()

  /**
   * 오늘 기준으로 이전 2일 계산
   */
  const prevDays: BroadcastWeekDays[] = useMemo(() => {
    return Array.from({ length: 2 }, (_, index) => {
      const prevDate = new Date(currentDate)
      prevDate.setDate(currentDate.getDate() - (index + 1))
      const formattedDate = formatYYYYMMDD({ date: prevDate })
      const isEmpty = broadcastCount ? broadcastCount[formattedDate] === 0 : true

      return {
        day: prevDate.toLocaleDateString('ko-KR', { weekday: 'short' }),
        date: prevDate,
        isSelected: selectedDate.getDate() === prevDate.getDate(),
        isEmpty,
      }
    }).reverse()
  }, [broadcastCount, selectedDate])

  const formattedDate = formatYYYYMMDD({ date: currentDate })
  const isEmpty = broadcastCount ? broadcastCount[formattedDate] === 0 : true
  const today: BroadcastWeekDays = {
    day: '오늘',
    date: currentDate,
    isSelected: isToday,
    isEmpty,
  }

  /**
   * 오늘 기준으로 다음 7일 계산
   */
  const nextDays: BroadcastWeekDays[] = useMemo(() => {
    return Array.from({ length: 7 }, (_, index) => {
      const nextDate = new Date(currentDate)
      nextDate.setDate(currentDate.getDate() + (index + 1))
      const formattedDate = formatYYYYMMDD({ date: nextDate })
      const isEmpty = broadcastCount ? broadcastCount[formattedDate] === 0 : true

      return {
        day: nextDate.toLocaleDateString('ko-KR', { weekday: 'short' }),
        date: nextDate,
        isSelected: selectedDate.getDate() === nextDate.getDate(),
        isEmpty,
      }
    })
  }, [broadcastCount, selectedDate])

  const weekDays: BroadcastWeekDays[] = [...prevDays, today, ...nextDays]

  const onClickWeek = useCallback(
    (date: Date) => {
      if (date.getTime() === selectedDate.getTime()) return
      setSelectedDate(date)
      resetMoreListIndex()
      resetPrevListIndex()
      resetIsPrevDataExist()
    },
    [resetIsPrevDataExist, resetMoreListIndex, resetPrevListIndex, selectedDate, setSelectedDate],
  )

  /**
   * 일자가 선택되지 않았을 경우 자동으로 오늘 선택
   * ex) 첫 진입 시 등
   */
  useEffect(() => {
    if (selectedDate.getDate() === 0) {
      setSelectedDate(currentDate)
    }
  }, [selectedDate, setSelectedDate])

  return {
    /**
     * 오늘 기준으로 이전 2일, 오늘, 오늘 기준으로 이후 7일
     * 총 10일 반환
     */
    weekDays,
    /**
     * 일자 클릭 핸들러
     */
    onClickWeek,
    /**
     * 각 일자의 방송 개수
     */
    broadcastCount,
  }
}
